import { cva } from 'class-variance-authority';

import { FLOATING_LABEL_MOTION } from '../styles/floatingLabelMotion.recipe';
import { focusRing } from '../styles/focusRing';
import { PHONE_INPUT_SIZE } from '../styles/phoneInputSize';

/**
 * Time picker class recipes — the field, and the portalled list of quarter-hour slots.
 *
 * Like `Button.recipe`, `TextInput.recipe`, `TextArea.recipe` and `DatePicker.recipe`, these
 * contain ZERO `dark:` variants: colour comes entirely from the `--ds-timepicker-*` slots that
 * `.ds-timepicker` sets in `packages/design-system/src/styles/design-system.css`.
 *
 * Every state selector on the FIELD reads Base UI's `Field.Root` attributes through the `group/ti`
 * marker (`data-focused`, `data-filled`, `data-invalid`, `data-disabled`); every state selector on
 * a LIST ROW reads Base UI's own item attributes (`data-highlighted`, `data-disabled`). There is no
 * React state behind either set, which also means the states are reachable from a story by
 * rendering.
 *
 * Both sets are MUTUALLY EXCLUSIVE rather than relying on utility sort order, the same discipline
 * every other recipe in this layer uses. Precedence, highest first:
 *
 *   field: disabled → invalid → focused → hover → resting
 *   row:   disabled → highlighted → resting
 *
 * ── KTD5: the field half is RESTATED from `DatePicker.recipe`, deliberately ───────────────────
 *
 * Everything from `timePickerRootRecipe` down to `timePickerHelperRecipe` is a near-copy of the
 * matching `datePicker*Recipe`, which is itself a near-copy of `textInputBoxRecipe`'s geometry.
 * That makes this the FOURTH hand-written copy, and it is a decision rather than an oversight.
 *
 * The reasons are the ones `TextArea.recipe` sets out at length and `DatePicker.recipe` repeats:
 * importing `.ds-input`'s class strings would drag another component's slot namespace into this
 * one, and each component owning its own namespace is what stops a change made for one breaking
 * another. Extracting a shared field shell instead would be the right long-term answer and it is a
 * cross-cutting refactor across four components that this ticket does not carry.
 *
 * What a fourth copy costs is one more chance to drift, and the third copy DID drift — most of
 * `DatePicker`'s Figma port was fixing it. So the same pin is taken here: `TimePicker.test.tsx`
 * imports this recipe and `TextInput.recipe` together and compares the radius, border width and
 * min-height they emit for every `position`, plus the numbers that actually moved last time (the
 * focus stroke, the hover fill, the label's shrunk scale, the control's padding).
 *
 * ── KTD7: there is NO Figma drawing for a time field ──────────────────────────────────────────
 *
 * Every value below is EXTRAPOLATED. The field half is taken from `Date Picker` 1438:129727 and the
 * popup and rows from `Menu` 556:20625 by way of `Select.recipe`, on the argument that a time field
 * is a text field whose popup is a list of options — but no cell in the library draws a time field
 * in any state, and nothing here has been confirmed by a designer. This follows the precedent set
 * by `Select`'s `trailing` slot, which is marked EXTRAPOLATED in its own types with the same
 * standing request. See the header of `TimePicker.tsx` for the full statement and for what it means
 * for review.
 */

/**
 * Marker + layout for `Field.Root`.
 *
 * The `.ds-timepicker` slot class lives HERE rather than on the bordered box, for the reason
 * `textInputRootRecipe` documents: custom properties cascade, so everything inside inherits them,
 * and anything that later renders as a SIBLING of the box would otherwise fall outside their scope
 * and resolve to inherited colour instead of failing visibly.
 *
 * The group marker is `group/ti` rather than a name of its own, shared with `TextInput`, `TextArea`
 * and `DatePicker` on purpose: they are alternative fields, never nested one inside another, so
 * there is no ambiguity to resolve — and sharing it means a stacked group's state selectors read
 * the same whichever field type is in it.
 */
export const timePickerRootRecipe = cva(['group/ti ds-timepicker', 'relative flex w-full flex-col']);

/**
 * The bordered box.
 *
 * Holds no padding of its own — all of it is on the `<input>`, exactly as on `TextInput`,
 * `TextArea` and `DatePicker` and for the same reason: padding the box would leave the input a
 * strip inside a larger box, so clicking near the edges would do nothing and the caret would land
 * in the wrong place. The only exception is the bottom inset when helper text is present, which the
 * control can no longer pay for because the helper sits below it.
 *
 * `min-h-[60px]` is the single-line field floor: 1 + 29 + 20 + 9 + 1. The pin test asserts the
 * match against `textInputBoxRecipe`.
 *
 * ── Why the stroke is drawn with a shadow, not the border ─────────────────────────────────────
 *
 * `border-width` NEVER changes here, in any state. A border is layout, so thickening it on focus
 * would move the padding box and shift the absolutely positioned label, the value text and the two
 * affordances pinned to the right gutter. So the border stays at its resting 1px and the focused
 * state's second pixel is painted as an inset `box-shadow`, which occupies no layout space:
 *
 *   inset 0 0 0 1px  --ds-timepicker-stroke-extra   the pixel that makes the focused stroke 2px
 *
 * The slot defaults to `transparent`, which is what lets the shadow rule be unconditional — only
 * the slot is switched, and only by focus.
 *
 * The field box takes no focus ring of its own: doing so would combine Active and Focus on an
 * ordinary pointer click, unlike the sibling text fields this component is designed to stack with.
 * The clear button inside it is separately focusable and does use the shared ring.
 */
export const timePickerBoxRecipe = cva(
  [
    'relative flex w-full flex-col gap-[4px]',
    'min-h-[60px]',
    'border-solid border-[length:var(--ds-timepicker-border-width)] border-[var(--ds-timepicker-border)]',
    'bg-[var(--ds-timepicker-bg)]',
    'cursor-text',
    'transition-[border-color,background-color,box-shadow] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    /**
     * Hover is excluded from every other state so it can never fight them, and it moves the FILL as
     * well as the border.
     *
     * The pointer-capability media guard is load-bearing and easy to lose. Tailwind's built-in
     * hover variant adds that guard automatically, but an arbitrary variant with a hand-written
     * hover selector does not. Without it, a tap on a touch device leaves the hovered fill and
     * border stuck until the user taps something else.
     */
    '[@media(hover:hover)]:group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:border-[var(--ds-timepicker-border-hover)]',
    '[@media(hover:hover)]:group-[:hover:not([data-disabled]):not([data-focused]):not([data-invalid])]/ti:bg-[var(--ds-timepicker-bg-hover)]',

    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:border-[var(--ds-timepicker-border-focus)]',
    'group-[[data-invalid]:not([data-disabled])]/ti:border-[var(--ds-timepicker-border-invalid)]',
    'group-data-[disabled]/ti:border-[var(--ds-timepicker-border-disabled)]',
    'group-data-[disabled]/ti:bg-[var(--ds-timepicker-bg-disabled)]',
    'group-data-[disabled]/ti:cursor-not-allowed',

    // Off by default, so the rule below can be unconditional.
    '[--ds-timepicker-stroke-extra:transparent]',
    'shadow-[inset_0_0_0_1px_var(--ds-timepicker-stroke-extra)]',

    // The second pixel, in the same colour as the focused border. FOCUS ONLY: error and disabled
    // are drawn at 1px across the whole field family.
    'group-[[data-focused]:not([data-disabled]):not([data-invalid])]/ti:[--ds-timepicker-stroke-extra:var(--ds-timepicker-border-focus)]',
  ],
  {
    variants: {
      /** Figma's `Position` axis, pinned to `TextInput`'s values by the test. */
      position: {
        standalone: 'rounded-[16px]',
        first: 'rounded-t-[16px] rounded-b-none',
        middle: 'rounded-none',
        last: 'rounded-b-[16px] rounded-t-none',
        // A field in a side-by-side row. The test pins these to `textInputBoxRecipe`, which says
        // why each value squares its other corners explicitly.
        'bottom-start': 'rounded-t-none rounded-ee-none rounded-es-[16px]',
        'bottom-end': 'rounded-t-none rounded-es-none rounded-ee-[16px]',
        start: 'rounded-e-none rounded-s-[16px]',
        end: 'rounded-s-none rounded-e-[16px]',
      },
      /**
       * The bottom inset, which moves between the box and the control when a helper row exists.
       *
       * `7px`, matching `textInputBoxRecipe` and `datePickerBoxRecipe`, so an errored time field is
       * not a pixel taller than the field beside it in a stacked group.
       */
      hasHelper: {
        true: 'pb-[7px]',
        false: 'pb-0',
      },
    },
    defaultVariants: { position: 'standalone', hasHelper: false },
  }
);

/**
 * The floating label.
 *
 * Mechanically identical to `textInputLabelRecipe` and `datePickerLabelRecipe` — same 19px resting
 * offset, same 9px shrunk offset, same 12/14 scale, same composited `translate`/`scale` animation —
 * so the field types stacked in one group have their labels on the same lines. That alignment is
 * the reason to copy the numbers rather than re-derive them.
 *
 * `max-w` clears both right-gutter affordances rather than the date field's one, so it subtracts
 * more: 24px left gutter + 24px right gutter + the 24px clear button + 8px + the 16px caret = 96.
 *
 * Deliberately NOT `pointer-events-none`. This is a real `<label>` wired to the control by `Field`,
 * so a click on it focuses the input natively; making it click-through would instead drop clicks on
 * the part of it that overhangs the control.
 */
export const timePickerLabelRecipe = cva([
  'absolute top-0 left-[24px] origin-left cursor-text select-none',
  'font-sans text-body-md text-[var(--ds-timepicker-label)]',
  'max-w-[calc(100%_-_96px)] truncate',
  'translate-y-[19px] scale-100',

  // `translate` and `scale`, not `transform`: Tailwind v4's utilities set the standalone CSS
  // properties of those names, so a transition on `transform` would match nothing and the label
  // would jump to its shrunk position before animating its size. `scale-100` is stated on the base
  // because interpolating from `scale: none` is not reliably animatable.
  FLOATING_LABEL_MOTION,

  // 12/14 — the shrunk size as a ratio of the resting one, since the font-size itself never changes.
  'group-data-[filled]/ti:translate-y-[9px] group-data-[filled]/ti:scale-[0.857143]',
  'group-data-[focused]/ti:translate-y-[9px] group-data-[focused]/ti:scale-[0.857143]',

  // There is deliberately no third rule for a visible placeholder: the format hint is transparent
  // until the field is focused (see `timePickerControlRecipe`), so `:placeholder-shown` never
  // describes anything the user can see and the two rules above are enough. `datePickerLabelRecipe`
  // carries the long version of this note.
  'group-data-[disabled]/ti:cursor-not-allowed group-data-[disabled]/ti:text-[var(--ds-timepicker-label-disabled)]',
]);

/** The required asterisk — `textInputRequiredMarkRecipe`'s rule, on this field's own slots. */
export const timePickerRequiredMarkRecipe = cva([
  'text-[var(--ds-timepicker-helper-invalid)]',
  'group-data-[disabled]/ti:text-[var(--ds-timepicker-label-disabled)]',
]);

/**
 * The `<input>`, which is also most of the field's hit area.
 *
 * `pt-[29px]` puts the value's 20px line box where `TextInput`, `TextArea` and `DatePicker` draw
 * theirs, so the four agree when stacked. Everything above that line is still part of the control
 * rather than dead space on the box.
 *
 * `pr-[80px]` is the one measurement that is not the date field's, because this field has TWO
 * right-gutter affordances rather than one. Right to left: a 24px gutter, a 16px caret, 8px of air,
 * a 24px clear button, then 8px before the value text may start — 24 + 16 + 8 + 24 + 8 = 80. The
 * caret and clear positions in `timePickerCaretRecipe` and `timePickerClearRecipe` are derived from
 * the same walk, so all three move together or not at all.
 *
 * Preflight is deliberately absent from the token layer, so the browser's own input styling is
 * still in play and every reset is spelled out rather than assumed.
 */
export const timePickerControlRecipe = cva(
  [
    'm-0 block w-full appearance-none border-0 bg-transparent',
    'font-sans text-body-md text-[var(--ds-timepicker-value)]',
    PHONE_INPUT_SIZE,
    'outline-none',
    'pt-[29px] pr-[80px] pl-[24px]',
    'cursor-text',
    /**
     * The format hint, which is HIDDEN until the field is focused.
     *
     * Hiding it with `text-transparent` rather than by adding or removing the attribute keeps it a
     * pure CSS state, so there is no focus state in React and no frame where the attribute is
     * mid-swap — and the input still reports `:placeholder-shown`, which is what an autofill
     * heuristic reads.
     *
     * Disabled deliberately never reveals it: a disabled field cannot be focused, so the rule below
     * simply never matches there.
     */
    'placeholder:text-transparent',
    'group-data-[focused]/ti:placeholder:text-[var(--ds-timepicker-placeholder)]',
    'disabled:cursor-not-allowed disabled:text-[var(--ds-timepicker-value-disabled)]',
    // Edge and Chrome add their own clear affordance, which would sit beside this field's.
    '[&::-ms-clear]:hidden',
  ],
  {
    variants: {
      /** When helper text follows, the bottom inset moves to the box — see `timePickerBoxRecipe`. */
      hasHelper: {
        true: 'pb-0',
        false: 'pb-[9px]',
      },
    },
    defaultVariants: { hasHelper: false },
  }
);

/**
 * The caret that says there is a list.
 *
 * A GLYPH, not a button, which is the opposite of the date field's calendar affordance and is
 * correct for the opposite reason. `DatePicker`'s trigger has to be a button because the calendar
 * has no other way in — the input is a text field and nothing else. This field IS the combobox: the
 * input itself carries `aria-expanded`, opens on click and on Down, and is the control a screen
 * reader is told about. A second focusable element beside it would add a tab stop that does nothing
 * the input does not already do, and WCAG 2.5.8's 24px minimum does not apply to something that is
 * not a target.
 *
 * So the geometry is `textInputAffixRecipe`'s decorative 16px cell — `right-[24px] top-[21px]` —
 * rather than the date field's 24px button box.
 */
export const timePickerCaretRecipe = cva([
  'pointer-events-none absolute top-[21px] right-[24px] inline-flex size-[16px] items-center justify-center',
  'text-[var(--ds-timepicker-caret)]',
  'transition-[color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'group-data-[disabled]/ti:text-[var(--ds-timepicker-caret-disabled)]',
]);

/**
 * The clear button — KTD8, the affordance the date field dropped and this one keeps.
 *
 * `DatePicker` has no clear control because its right gutter was already spent on the calendar
 * trigger. This field's trigger is the input, so the gutter has room, and `Autocomplete.Clear` is a
 * real Base UI part with the mount/unmount and the value reset already written. The field being
 * replaced shows one too, so dropping it would be a regression a user could feel.
 *
 * 24px round, the drawing's `Button Size=Small Type=Muted iconOnly`, sitting 8px to the left of the
 * caret: 24px gutter + 16px caret + 8px gap puts its right edge 48px in. That is a real target, so
 * unlike the caret it is held to WCAG 2.5.8 — 24×24 clears the minimum, and the pseudo-element
 * below grows the comfort target the way `datePickerTriggerRecipe`'s does without moving anything
 * visible.
 *
 * `focus-visible:` rather than the box's state, because this element CAN be focused and the point
 * of a ring here is to distinguish reaching the button from reaching the input.
 */
export const timePickerClearRecipe = cva([
  'absolute top-[17px] right-[48px] inline-flex size-[24px] items-center justify-center',
  'm-0 cursor-pointer appearance-none rounded-full border-0 bg-transparent p-0',
  'text-[var(--ds-timepicker-clear-fg)]',
  'transition-[color,background-color] duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  'hover:not-disabled:bg-[var(--ds-timepicker-clear-bg-hover)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ds-timepicker-clear-fg-disabled)]',
  focusRing(),
  '[&_svg]:size-[16px]',

  // No 300ms double-tap-zoom wait before the field clears on a phone.
  'touch-manipulation',

  /**
   * The HIT AREA, grown past the 24px the button draws.
   *
   * 44px tall (10px each way, still inside the 60px box) and 4px out each side — it cannot grow
   * into the caret to its right or under the value text to its left without stealing a tap that
   * means something else, which is the same trade `datePickerTriggerRecipe` documents.
   */
  "after:absolute after:-inset-y-[10px] after:-inset-x-[4px] after:content-['']",
]);

/**
 * Helper / error text, rendered inside the border as the other three fields do.
 *
 * Inside rather than below, and that placement is what makes the stacked group work: `FieldGroup`
 * collapses the borders of adjacent CHILDREN, so anything rendered outside the box would sit
 * between two borders that are supposed to be one line.
 */
export const timePickerHelperRecipe = cva([
  'flex items-center',
  // `m-0` is load-bearing rather than tidiness: `Field.Description` renders a `<p>`, and with
  // Preflight absent it keeps the browser's default `margin: 1em 0`, which the `<div>` that
  // `Field.Error` renders does not have — so the box would change height with the state.
  'm-0',
  'px-[24px]',
  'font-sans text-body-sm text-[var(--ds-timepicker-helper)]',
  'group-[[data-invalid]:not([data-disabled])]/ti:text-[var(--ds-timepicker-helper-invalid)]',
  'group-data-[disabled]/ti:text-[var(--ds-timepicker-label-disabled)]',
]);

/** The positioner. `z-[1400]` is the layer `Select`, `Menu` and `DatePicker` put their popups on. */
export const timePickerPositionerRecipe = cva(['z-[1400]']);

/**
 * The popup holding the slot list.
 *
 * `ds-timepicker` is repeated here by the component rather than inherited: the popup is portalled
 * into `<body>`, so it has none of the field's ancestors and would otherwise resolve every slot to
 * nothing. The theme class is NOT repeated — `TimePicker` puts it on `Autocomplete.Portal`, which
 * is an ancestor of this element, so the ordinary cascade reaches it. `selectMenuRecipe` and
 * `datePickerPopupRecipe` document the same pair of facts.
 *
 * The geometry is the `Menu` panel's, by way of `selectMenuRecipe`: 16px radius, 8px padding,
 * pinned to `--anchor-width` so the list is exactly as wide as the field it belongs to.
 *
 * ── The height is capped at nine rows, which is this panel's one departure ────────────────────
 *
 * `max-h-[var(--available-height)]` alone is what the other popups use, and on a tall viewport it
 * would render all 96 rows — a list longer than the page, whose scrollbar is the only clue that it
 * is a list of times rather than the page itself. 9 rows is a little over two hours of choices at
 * a glance, and it is what the field being replaced shows. `min()` keeps the collision cap as the
 * harder of the two, so near the bottom of the viewport the panel still shrinks to fit.
 *
 *   9 rows x 36px + 8 gaps x 2px + 16px panel padding + 2px border = 358px
 *
 * The border is part of that sum because the panel is `box-border`, so `max-height` bounds the
 * BORDER box rather than the content box. The first version of this derivation left it out and
 * wrote 356px, which clipped the ninth row by exactly 2px - caught by `PopupGeometry` in
 * `TimePicker.stories`, which is the only layer that can see rendered geometry at all.
 */
export const timePickerPopupRecipe = cva([
  'ds-timepicker',
  'box-border flex flex-col',
  'w-[var(--anchor-width)]',
  'max-h-[min(358px,var(--available-height))] overflow-y-auto overscroll-contain',
  'p-[8px]',
  'rounded-[16px]',
  'border-solid border-[length:1px] border-[var(--ds-timepicker-popup-border)]',
  'bg-[var(--ds-timepicker-popup-bg)]',
  'outline-none',

  /**
   * ── The entrance and the exit ────────────────────────────────────────────────────────────────
   *
   * The same overlay motion `menuPanelRecipe`, `selectMenuRecipe` and `datePickerPopupRecipe`
   * carry, for the same reason the four share their geometry: a list opening off a field is the
   * same kind of event whichever field it is, and the user should not be able to tell from the
   * motion which one they are looking at. `--motion-overlay` in, `--motion-overlay-exit` out,
   * origin-aware, scale gated on `motion-safe:`.
   *
   * The reasoning is written once, on `menuPanelRecipe`.
   */
  '[transform-origin:var(--transform-origin,center)]',
  'transition-[opacity,scale] duration-[var(--motion-overlay)] ease-[var(--motion-overlay-ease)]',
  'data-[ending-style]:duration-[var(--motion-overlay-exit)]',
  'data-[ending-style]:ease-[var(--motion-overlay-exit-ease)]',
  'data-[starting-style]:opacity-0 data-[ending-style]:opacity-0',
  'motion-safe:data-[starting-style]:scale-[0.96] motion-safe:data-[ending-style]:scale-[0.96]',
]);

/**
 * The listbox inside the panel.
 *
 * Exists ONLY to carry the 2px row gap, and it has to be here rather than on the panel:
 * `Autocomplete.List` is a single flex child of the popup, so a `gap` on the popup would separate
 * the list from its siblings and nothing inside it. `selectListRecipe` says the same.
 */
export const timePickerListRecipe = cva(['flex flex-col gap-[2px]']);

/**
 * One slot row.
 *
 * A slot IS an option row, so this is `selectItemRecipe`'s treatment rather than a new one — the
 * same 36px row (12px across, 8px down, a 14px `Label/lg` line), the same 10px radius, the same
 * hover-and-keyboard-highlight fill, the same panel-aware focus ring. Restated rather than imported
 * for the namespace reason in this file's header; the numbers are the ones read off the reworked
 * `List-Item` set (node 82:7424) on 2026-09-15, and the 36px row height is what
 * `timePickerPopupRecipe`'s nine-row cap is computed from.
 *
 * `data-highlighted` covers BOTH mouse hover and keyboard navigation, because Base UI's
 * `highlightItemOnHover` defaults to true and the cells draw one treatment for both.
 *
 * There is no selected state and no tick. A time field commits one value and the input already
 * shows it, and — unlike a select — the list is FILTERED by that value as you type, so the
 * committed row is usually the only row there. Selector precedence stays mutually exclusive:
 * disabled → highlighted → resting.
 */
export const timePickerItemRecipe = cva([
  'group/tpi',
  'relative flex w-full shrink-0 items-center gap-[8px]',
  'm-0 appearance-none border-0 bg-transparent text-left',
  // 44px rows on phones, matching `menuItemRecipe`: the list is tapped, not pointed at.
  'px-[12px] py-[8px] max-sm:py-[12px]',
  'rounded-sm-md',
  'font-sans text-label-lg select-none',
  'outline-none',
  'cursor-pointer',
  /**
   * No transition, on purpose. `data-highlighted` moves under the arrow keys as well as the
   * pointer, and a 150ms tint made the highlight TRAIL the keyboard — the row being left was still
   * fading out when the next key landed, so a fast run down the list smeared across three rows.
   * That is worse here than in a menu: this list is 96 rows long, so holding Down is the ordinary
   * way to reach an evening slot rather than an edge case. `menuItemRecipe` and `selectItemRecipe`
   * make the same call for the same reason.
   */

  // Resting, out-specified by everything below. The transparent row inherits the panel's fill,
  // which also keeps it correct if a caller themes the panel.
  'text-[var(--ds-timepicker-item-fg)]',

  'data-[highlighted]:not-data-[disabled]:bg-[var(--ds-timepicker-item-bg-hover)]',
  'data-[highlighted]:not-data-[disabled]:text-[var(--ds-timepicker-item-fg-hover)]',

  /**
   * `State=Focus`, the design system's shared two-shadow ring with a PANEL-AWARE inner gap.
   *
   * The gap reads `--ds-timepicker-item-focus-gap`, which the class points at its own panel fill,
   * rather than `effect/focus-inner` — that role carries the two `background/primary` values, and
   * dark mode's does not match this control surface. `--ds-select-item-focus-gap` makes the same
   * substitution for the same reason, and `menuItemRecipe` documents the whole thing at length.
   */
  focusRing('timePickerItem'),

  /**
   * Disabled, which RECOLOURS rather than dims, per `contrast.md`: a multiplier is a value no
   * contrast harness can read without compositing it first.
   *
   * No row is disabled today — `min` removes rows rather than disabling them, which is the legacy
   * behaviour the bridge has to reproduce exactly. The rule is here because `Autocomplete.Item`
   * takes a `disabled` prop that a future caller could reach, and a row that could be disabled
   * without looking disabled is worse than one that cannot be disabled at all.
   */
  'data-[disabled]:cursor-not-allowed data-[disabled]:text-[var(--ds-timepicker-item-fg-disabled)]',
]);
